import { projectMetaKeys } from '@/hooks/meta/useProjectMeta'
import { projectFields } from '@/lib/field-sets'
import { queryKeys } from '@/lib/query-keys'
import { invalidateTenantQueries } from '@/services/query/tenant-queries'
import { supabase } from '@/services/supabase/supabase'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// Define the Project type
export interface Project {
  id: string
  name: string
  description: string | null
  genre: string | null
  created_at: string
  updated_at: string | null
  current_story_id?: string | null
  settings?: Record<string, any> | null
}

/**
 * Hook to fetch all projects
 * @param options Additional options for the query
 */
export function useProjects(options: { enabled?: boolean } = {}) {

  return useQuery({
    queryKey: queryKeys.projects.lists(),
    queryFn: async () => {
      console.log('Fetching projects list from Supabase...');
      const { data, error } = await supabase
        .from('projects')
        .select('*') // Select all fields including settings
        .order('created_at', { ascending: false })

      if (error) throw error
      console.log('Projects fetched:', data?.length || 0);
      // Return empty array if no data
      return (data || []) as unknown as Project[]
    },
    staleTime: 0, // Consider data stale immediately
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus
    ...options,
  })
}

/**
 * Hook to fetch a single project by ID
 * @param projectId The project ID to fetch
 * @param options Additional options for the query
 */
export function useProject(
  projectId: string | undefined,
  options: { enabled?: boolean; includeCurrentStory?: boolean } = {}
) {
  const { includeCurrentStory = false, ...restOptions } = options

  return useQuery({
    queryKey: queryKeys.projects.detail(projectId || ''),
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      const { data, error } = await supabase
        .from('projects')
        .select(includeCurrentStory ? projectFields.withCurrentStory : projectFields.detail)
        .eq('id', projectId)
        .single()

      if (error) throw error
      return data as unknown as Project
    },
    enabled: !!projectId && (restOptions.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Hook to fetch just the name of a project
 * @param projectId The project ID to fetch
 * @param options Additional options for the query
 */
export function useProjectName(
  projectId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: queryKeys.projects.detail(projectId || ''),
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      const { data, error } = await supabase
        .from('projects')
        .select('name,current_story_id')
        .eq('id', projectId)
        .single()

      if (error) throw error
      return data as { name: string; current_story_id?: string }
    },
    enabled: !!projectId && (options.enabled !== false),
  })
}

/**
 * Hook to create a new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (newProject: Pick<Project, 'name' | 'description' | 'genre'>) => {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          name: newProject.name,
          description: newProject.description || '',
          genre: newProject.genre || '',
        })
        .select()
        .single()

      if (error) throw error
      return data as Project
    },
    onSuccess: (data) => {
      // Update the projects list query
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.lists(),
      })

      // Add the new project to the cache
      queryClient.setQueryData(
        queryKeys.projects.detail(data.id),
        data
      )

      // Invalidate tenant cache since project creation might affect tenant context
      invalidateTenantQueries(queryClient)
    },
  })
}

/**
 * Hook to update a project
 */
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Pick<Project, 'id'> & Partial<Omit<Project, 'id'>>) => {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Project
    },
    onSuccess: (data) => {
      console.log('Project update successful, invalidating caches for project:', data.id);

      // Update the project detail in cache
      queryClient.setQueryData(
        queryKeys.projects.detail(data.id),
        data
      )

      // Update the project in the projects list cache directly
      queryClient.setQueryData(
        queryKeys.projects.lists(),
        (oldProjects: Project[] | undefined) => {
          if (!oldProjects) return oldProjects;
          return oldProjects.map(project =>
            project.id === data.id ? { ...project, ...data } : project
          );
        }
      )

      // Invalidate the projects list to ensure UI updates - be more aggressive
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.lists(),
        refetchType: 'active', // Force refetch of active queries
      })

      // Also invalidate all project-related queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.all,
        refetchType: 'active',
      })

      // Force refetch of the projects list specifically
      queryClient.refetchQueries({
        queryKey: queryKeys.projects.lists(),
      })

      // Invalidate any story queries that might depend on the current_story_id
      if ('current_story_id' in data) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.stories.byProject(data.id),
        })
      }

      // Invalidate project meta cache
      if (data.id) {
        queryClient.invalidateQueries({
          queryKey: projectMetaKeys.detail(data.id)
        });
      }

      // Invalidate tenant cache since project updates might affect tenant context
      invalidateTenantQueries(queryClient)

      console.log('Cache invalidation completed for project update');
    },
  })
}

/**
 * Hook to delete a project
 */
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (projectId: string) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId)

      if (error) throw error
      return projectId
    },
    onSuccess: (projectId) => {
      console.log('Project deletion successful, updating caches for project:', projectId);

      // Remove the project from the cache
      queryClient.removeQueries({
        queryKey: queryKeys.projects.detail(projectId),
      })

      // Remove project meta cache
      queryClient.removeQueries({
        queryKey: projectMetaKeys.detail(projectId)
      })

      // Directly remove the project from the projects list cache
      queryClient.setQueryData(
        queryKeys.projects.lists(),
        (oldProjects: Project[] | undefined) => {
          if (!oldProjects) return oldProjects;
          return oldProjects.filter(project => project.id !== projectId);
        }
      )

      // Invalidate the projects list to ensure UI updates - be more aggressive
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.lists(),
        refetchType: 'active', // Force refetch of active queries
      })

      // Also invalidate all project-related queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.all,
        refetchType: 'active',
      })

      // Force refetch of the projects list specifically
      queryClient.refetchQueries({
        queryKey: queryKeys.projects.lists(),
      })

      // Invalidate any stories related to this project
      queryClient.invalidateQueries({
        queryKey: queryKeys.stories.byProject(projectId),
      })

      // Invalidate tenant cache since project deletion might affect tenant context
      invalidateTenantQueries(queryClient)

      console.log('Cache invalidation completed for project deletion');
    },
  })
}

/**
 * Hook to manually refresh the projects list
 * Use this when you need to force a refresh of project data
 */
export function useRefreshProjects() {
  const queryClient = useQueryClient()

  return () => {
    console.log('Manually refreshing projects list...');

    // Clear all project-related caches
    queryClient.invalidateQueries({
      queryKey: queryKeys.projects.all,
    });

    // Force refetch of projects list
    queryClient.refetchQueries({
      queryKey: queryKeys.projects.lists(),
    });

    console.log('Projects list refresh completed');
  }
}